/**
 * <mb-facets>: filtros da listagem e da busca (substitui o componente products-filter.v14 da Vnda).
 * - Desktop: balão aberto/fechado pelo botão "+ FILTRAR PRODUTOS"; mobile: drawer pela esquerda com trava de rolagem.
 * - Qualquer mudança aplica na hora: monta a URL a partir do form, busca a seção por Section Rendering,
 *   troca o conteúdo e atualiza o histórico (sem recarregar a página).
 * - data-single-select="true" limita a seleção a um valor por grupo (D-16; o padrão é múltipla, nativa).
 * - Preço: dois range sincronizados alimentam os campos ocultos filter.v.price.gte/lte, desativados nos extremos.
 * - Dispara mb:facets:applied depois de cada aplicação.
 * - Com algum filtro aplicado, a URL leva filter.v.availability=1 (só produtos disponíveis).
 * - data-layout="sidebar": no computador o painel é a coluna da esquerda (sempre visível, presa abaixo do
 *   cabeçalho) e cada grupo abre/fecha pelo título; no mobile continua o drawer.
 */
const { fetchSection, replaceSection, trapFocus, emit, formatMoney } = window.MB;

/** Seções com o painel aberto, para reabrir depois da troca de conteúdo. */
const openSections = new Set();

/** Grupos que o cliente abriu/fechou no filtro lateral ("seção|param" → aberto), para manter depois da troca de conteúdo. */
const groupState = new Map();

const desktopQuery = window.matchMedia('(min-width: 992px)');

class MbFacets extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('.facets-form');
    if (!this.form) return;
    this.sectionId = this.dataset.sectionId;
    this.panel = this.querySelector('[data-facets-panel]');
    this.toggleButton = this.querySelector('[data-facets-toggle]');
    this.toggleText = this.querySelector('.facets-toggle-text');
    this.singleSelect = this.dataset.singleSelect === 'true';
    this.sidebar = this.dataset.layout === 'sidebar';

    this.toggleButton?.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
    this.querySelectorAll('[data-facets-close]').forEach((button) => button.addEventListener('click', () => this.close()));
    this.panel?.addEventListener('click', (event) => {
      if (event.target === this.panel && !this.isSidebar) this.close();
    });
    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close();
    };
    document.addEventListener('keydown', this.onKeydown);

    this.form.addEventListener('change', (event) => this.onChange(event));
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.apply();
    });

    this.querySelectorAll('[data-facets-show-more]').forEach((button) => {
      button.addEventListener('click', () => this.toggleShowMore(button));
    });

    this.querySelector('[data-facets-clear]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.apply(event.currentTarget.getAttribute('href'));
    });

    this.setupPrice();
    if (this.sidebar) this.setupSidebar();

    if (openSections.has(this.sectionId)) this.open({ silent: true });
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    desktopQuery.removeEventListener('change', this.onViewportChange);
    this.headerObserver?.disconnect();
    this.releaseFocus?.();
  }

  get isMobile() {
    return window.matchMedia('(max-width: 991px)').matches;
  }

  /** Filtro lateral visível (computador); no mobile o mesmo painel é o drawer. */
  get isSidebar() {
    return this.sidebar && desktopQuery.matches;
  }

  /* ----- Filtro lateral ----- */

  setupSidebar() {
    this.accordions = [...this.querySelectorAll('[data-facets-accordion]')];
    this.accordions.forEach((button) => {
      const group = button.closest('[data-facets-group]');
      const saved = groupState.get(`${this.sectionId}|${group.dataset.param}`);
      if (saved !== undefined) group.classList.toggle('-collapsed', !saved);
      button.addEventListener('click', () => {
        if (!this.isSidebar) return;
        const open = group.classList.toggle('-collapsed') === false;
        groupState.set(`${this.sectionId}|${group.dataset.param}`, open);
        this.syncAccordions();
      });
    });
    this.onViewportChange = () => this.syncAccordions();
    desktopQuery.addEventListener('change', this.onViewportChange);
    this.syncAccordions();

    // A coluna fica presa logo abaixo do cabeçalho fixo; a altura dele vira --facets-top no contêiner.
    this.column = this.closest('.filter-container');
    const header = document.querySelector('.header-section');
    if (!this.column || !header) return;
    const setTop = () => {
      const fixed = getComputedStyle(header).position === 'sticky';
      this.column.style.setProperty('--facets-top', `${fixed ? header.offsetHeight : 0}px`);
    };
    setTop();
    this.headerObserver = new ResizeObserver(setTop);
    this.headerObserver.observe(header);
  }

  /** No mobile os grupos ficam sempre abertos (como antes), então o aria-expanded acompanha o que aparece. */
  syncAccordions() {
    this.accordions.forEach((button) => {
      const collapsed = button.closest('[data-facets-group]').classList.contains('-collapsed');
      button.setAttribute('aria-expanded', String(!this.isSidebar || !collapsed));
    });
  }

  /** Drawer/balão aberto. No filtro lateral o painel não usa `hidden` (seria escondido também no computador). */
  get isOpen() {
    if (!this.panel) return false;
    return this.sidebar ? this.panel.classList.contains('-open') : !this.panel.hidden;
  }

  setPanelOpen(open) {
    if (this.sidebar) this.panel.classList.toggle('-open', open);
    else this.panel.hidden = !open;
  }

  open({ silent = false } = {}) {
    if (!this.panel) return;
    this.setPanelOpen(true);
    this.toggleButton?.setAttribute('aria-expanded', 'true');
    if (this.toggleText) this.toggleText.textContent = this.toggleText.dataset.closeText;
    openSections.add(this.sectionId);
    if (this.isMobile) {
      document.documentElement.classList.add('mm-ocd--open');
      if (!silent) this.releaseFocus = trapFocus(this.panel, this.querySelector('[data-facets-close]'));
    }
  }

  close() {
    if (!this.panel) return;
    this.setPanelOpen(false);
    this.toggleButton?.setAttribute('aria-expanded', 'false');
    if (this.toggleText) this.toggleText.textContent = this.toggleText.dataset.openText;
    openSections.delete(this.sectionId);
    document.documentElement.classList.remove('mm-ocd--open');
    this.releaseFocus?.();
    this.releaseFocus = null;
    this.toggleButton?.focus();
  }

  toggleShowMore(button) {
    const group = button.closest('[data-facets-group]');
    const expanded = button.dataset.expanded === 'true';
    group.querySelectorAll('[data-facets-extra]').forEach((item) => item.classList.toggle('-visible', !expanded));
    button.dataset.expanded = String(!expanded);
    button.textContent = expanded ? button.dataset.moreText : button.dataset.lessText;
  }

  onChange(event) {
    const input = event.target;
    if (input.matches('[data-price-range]')) return;
    if (this.singleSelect && input.type === 'checkbox' && input.checked) {
      const group = input.closest('[data-facets-group]');
      group?.querySelectorAll('input[type="checkbox"]').forEach((other) => {
        if (other !== input) other.checked = false;
      });
    }
    this.apply();
  }

  /* ----- Preço ----- */

  setupPrice() {
    this.price = this.querySelector('[data-price]');
    if (!this.price) return;
    this.priceMax = Number(this.price.dataset.max) || 0;
    this.rangeMin = this.price.querySelector('[data-price-range="min"]');
    this.rangeMax = this.price.querySelector('[data-price-range="max"]');
    this.inputMin = this.price.querySelector('[data-price-input="min"]');
    this.inputMax = this.price.querySelector('[data-price-input="max"]');
    this.labelMin = this.price.querySelector('[data-price-label="min"]');
    this.labelMax = this.price.querySelector('[data-price-label="max"]');
    this.fill = this.price.querySelector('[data-price-fill]');

    [this.rangeMin, this.rangeMax].forEach((range) => {
      range?.addEventListener('input', () => this.onPriceInput(range));
      range?.addEventListener('change', () => this.applyPrice());
    });

    this.price.querySelectorAll('[data-price-tier]').forEach((button) => {
      button.addEventListener('click', () => {
        const [min, max] = button.dataset.priceTier.split('|').map(Number);
        this.rangeMin.value = min;
        this.rangeMax.value = Math.min(max, this.priceMax);
        this.onPriceInput(this.rangeMin);
        this.applyPrice();
      });
    });

    this.paintPrice();
  }

  onPriceInput(changed) {
    let min = Number(this.rangeMin.value);
    let max = Number(this.rangeMax.value);
    if (min > max) {
      if (changed === this.rangeMin) {
        max = min;
        this.rangeMax.value = max;
      } else {
        min = max;
        this.rangeMin.value = min;
      }
    }
    this.paintPrice();
  }

  paintPrice() {
    const min = Number(this.rangeMin.value);
    const max = Number(this.rangeMax.value);
    const total = this.priceMax || 1;
    if (this.fill) {
      this.fill.style.left = `${(min / total) * 100}%`;
      this.fill.style.width = `${((max - min) / total) * 100}%`;
    }
    if (this.labelMin) this.labelMin.textContent = formatMoney(min * 100);
    if (this.labelMax) this.labelMax.textContent = formatMoney(max * 100);
  }

  applyPrice() {
    const min = Number(this.rangeMin.value);
    const max = Number(this.rangeMax.value);
    this.inputMin.value = min;
    this.inputMax.value = max;
    this.inputMin.disabled = min <= 0;
    this.inputMax.disabled = max >= this.priceMax;
    this.apply();
  }

  /* ----- Aplicação ----- */

  url() {
    const params = new URLSearchParams(new FormData(this.form));
    for (const [key, value] of [...params]) {
      if (value === '') params.delete(key);
    }
    // A ordenação padrão da coleção não precisa ir na URL (mantém o link limpo, como no site atual).
    if (params.get('sort_by') === this.dataset.defaultSort) params.delete('sort_by');
    // Com qualquer filtro aplicado, só aparece o que está disponível (ex.: tamanho P esgotado não entra no filtro P).
    // O grupo "Disponibilidade" do Search & Discovery não é desenhado (snippets/facets.liquid).
    if ([...params.keys()].some((key) => key.startsWith('filter.'))) params.set('filter.v.availability', '1');
    const query = params.toString();
    return query ? `${this.form.action}?${query}` : this.form.action;
  }

  async apply(destination) {
    const url = destination || this.url();
    this.classList.add('-loading');
    // O filtro lateral é re-renderizado junto com a grade: guarda a rolagem da coluna e o campo em foco.
    const columnScroll = this.isSidebar ? this.column?.scrollTop : 0;
    const focusedId = this.contains(document.activeElement) ? document.activeElement.id : '';
    try {
      const doc = await fetchSection(this.sectionId, url);
      window.history.pushState({ facets: true }, '', url);
      const section = replaceSection(this.sectionId, doc);
      if (columnScroll) {
        const column = section?.querySelector('.filter-container');
        if (column) column.scrollTop = columnScroll;
      }
      if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });
      emit('mb:facets:applied', { url });
    } catch (error) {
      window.location.href = url;
    } finally {
      this.classList.remove('-loading');
    }
  }
}

if (!customElements.get('mb-facets')) customElements.define('mb-facets', MbFacets);

/** Voltar/avançar do navegador refaz a listagem sem recarregar a página. */
window.addEventListener('popstate', async (event) => {
  if (!event.state || !event.state.facets) return;
  const facets = document.querySelector('mb-facets');
  if (!facets) return;
  const doc = await fetchSection(facets.dataset.sectionId, window.location.href).catch(() => null);
  if (doc) replaceSection(facets.dataset.sectionId, doc);
});
